/*
 * Google Tag Manager "per noleggio" (tenant).
 *
 * - Ogni noleggio puo' avere il proprio container GTM (datiNoleggio.googleTagManagerId).
 *   Se non e' valorizzato (o non e' nel formato GTM-XXXX) non viene fatto nulla.
 * - Prima del caricamento di gtm.js viene impostato Consent Mode v2 con tutti i
 *   consensi "denied" (tranne security_storage). Se l'utente ha gia' espresso il
 *   consenso per questo noleggio (vedi utils/consent.js) viene applicato subito,
 *   prima di gtm.js; altrimenti il banner (CookieConsent) lo raccoglie.
 * - Il hook restituisce true quando GTM e' attivo per il noleggio corrente.
 * - pushToGtm / isGtmActiveFor servono agli eventi ecommerce (utils/ecommerce.js).
 * - Se durante la vita della pagina si passa a un noleggio diverso (o senza GTM)
 *   dopo che un container e' stato caricato, la pagina viene ricaricata cosi' che
 *   il container del noleggio precedente non "trapeli" nel nuovo shop.
 * - Ad ogni cambio di location viene inviato l'evento virtual_page_view al dataLayer
 *   (solo se GTM e' attivo per il noleggio corrente).
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { readConsent, toConsentModeState } from "../utils/consent";

const GTM_ID_REGEX = /^GTM-[A-Z0-9]{4,10}$/;
const GTM_SCRIPT_ID = "gtm-script";

// Stato a livello di modulo: vale per tutta la vita della pagina
let loadedGtmId = null;
let loadedTenant = null;
let reloading = false;
// Push in attesa che GTM sia caricato (es. purchase inviato prima dei dati noleggio)
let pendingPushes = [];

function isValidGtmId(gtmId) {
  return typeof gtmId === "string" && GTM_ID_REGEX.test(gtmId);
}

function sameTenant(a, b) {
  return !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();
}

// true se GTM e' gia' caricato per questo noleggio
export function isGtmActiveFor(codiceNoleggio) {
  return loadedGtmId !== null && sameTenant(loadedTenant, codiceNoleggio);
}

// Invia al dataLayer solo se GTM e' attivo per il noleggio; altrimenti accoda e
// invia dopo il caricamento (cosi' resta dopo consent default e gtm.js).
// Se il noleggio non ha GTM la coda non viene mai inviata.
export function pushToGtm(codiceNoleggio, data) {
  if (isGtmActiveFor(codiceNoleggio)) {
    window.dataLayer.push(data);
    return;
  }
  pendingPushes.push({ codiceNoleggio, data });
}

function flushPending() {
  const toSend = pendingPushes.filter((p) =>
    sameTenant(p.codiceNoleggio, loadedTenant)
  );
  pendingPushes = [];
  toSend.forEach((p) => window.dataLayer.push(p.data));
}

function loadGtm(gtmId, codiceNoleggio) {
  window.dataLayer = window.dataLayer || [];

  function gtag() {
    window.dataLayer.push(arguments);
  }

  // Consent Mode v2: default negati PRIMA del caricamento di GTM
  gtag("consent", "default", {
    ad_storage: "denied",
    analytics_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "granted",
    wait_for_update: 500,
  });

  // Consenso gia' espresso in una visita precedente: applicato prima di gtm.js
  const stored = readConsent(codiceNoleggio);
  if (stored) {
    gtag("consent", "update", toConsentModeState(stored));
  }

  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
    codiceNoleggio,
  });

  const script = document.createElement("script");
  script.async = true;
  script.id = GTM_SCRIPT_ID;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
    gtmId
  )}`;
  document.head.appendChild(script);
}

function useGoogleTagManager(gtmId, codiceNoleggio) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const lastPageViewKey = useRef(null);
  const [loaded, setLoaded] = useState(false);

  const validId = isValidGtmId(gtmId);
  // Il noleggio e' "noto" solo quando i dati sono stati caricati
  const active = validId && !!codiceNoleggio;

  // Caricamento del container / gestione cambio noleggio
  useEffect(() => {
    if (!codiceNoleggio || reloading) {
      return;
    }

    if (loadedGtmId !== null) {
      // Un container e' gia' stato caricato in questa pagina
      if (gtmId !== loadedGtmId || codiceNoleggio !== loadedTenant) {
        reloading = true;
        window.location.reload();
      }
      return;
    }

    if (!isValidGtmId(gtmId)) {
      return;
    }

    if (document.getElementById(GTM_SCRIPT_ID)) {
      // Script gia' presente (es. hot reload in sviluppo): non lo reinietto
      loadedGtmId = gtmId;
      loadedTenant = codiceNoleggio;
      flushPending();
      setLoaded(true);
      return;
    }

    loadGtm(gtmId, codiceNoleggio);
    loadedGtmId = gtmId;
    loadedTenant = codiceNoleggio;
    flushPending();
    setLoaded(true);
  }, [gtmId, codiceNoleggio]);

  // Virtual page view: dichiarato dopo l'effect di caricamento, quindi nello stesso
  // commit viene eseguito dopo il push di gtm.js
  useEffect(() => {
    if (!active || reloading || loadedGtmId !== gtmId) {
      return;
    }

    // L'URL deve appartenere al noleggio caricato: durante un cambio noleggio i dati
    // in redux possono essere ancora quelli del precedente (poi la pagina si ricarica)
    if (
      !location.pathname
        .toLowerCase()
        .includes(`/${codiceNoleggio.toLowerCase()}`)
    ) {
      return;
    }

    const pagePath = location.pathname + location.search;
    const key = `${codiceNoleggio}|${pagePath}`;
    if (lastPageViewKey.current === key) {
      return; // niente doppio invio
    }
    lastPageViewKey.current = key;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "virtual_page_view",
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
      codiceNoleggio,
      lang: i18n.language,
    });
  }, [
    active,
    gtmId,
    codiceNoleggio,
    location.pathname,
    location.search,
    i18n.language,
  ]);

  return active && loaded && loadedGtmId === gtmId;
}

export default useGoogleTagManager;
export { useGoogleTagManager };
